import { useFeature, useGrowthBook } from '@growthbook/growthbook-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { splunkInstance } from '@/common/services/splunk';

/**
 * Online-visit VAT: the invoice (service-price API) charges exactly 3% of the service price — checked on
 * 40 doctors on 2026-10-06 (price + vat == payable_cost every time).
 */
export const ONLINE_VISIT_VAT_RATE = 0.03;

/** GrowthBook A/B (50% rollout on `id` = terminal_id): show online-visit prices with the VAT included. */
export const ONLINE_VISIT_PRICE_WITH_VAT_FLAG = 'online-visit-price-with-vat';

const STORAGE_KEY = 'p24_price_vat';

type Group = 'with_vat' | 'control';

/** The amount the patient will pay on the invoice, in the same unit as `amount` (rial or toman). */
export const withOnlineVisitVat = (amount: number, includeVat: boolean) =>
  includeVat ? Math.round(amount * (1 + ONLINE_VISIT_VAT_RATE)) : amount;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Pages are CDN-cached, so the server always renders today's price and the client switches it. To keep
// that switch as early as possible, the group from the previous page load is kept in localStorage and
// applied in a layout effect (before the hydrated page paints); GrowthBook only fills it in when nothing
// is stored yet. Once decided, it never changes during the page load.
let decided: Group | null = null;
const loggedSurfaces = new Set<string>();

const readStored = (): Group | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'with_vat' || v === 'control' ? v : null;
  } catch {
    return null;
  }
};

export const useOnlineVisitPriceWithVat = (surface: 'search' | 'profile' | 'yara'): boolean => {
  const growthbook = useGrowthBook();
  const feature = useFeature(ONLINE_VISIT_PRICE_WITH_VAT_FLAG);
  // `decided` is only set on the client after hydration, so the first render always matches the server.
  const [group, setGroup] = useState<Group | null>(decided);
  const live: Group | null =
    feature.source === 'unknownFeature' || !growthbook?.getAttributes()?.id ? null : feature.on ? 'with_vat' : 'control';

  useIsomorphicLayoutEffect(() => {
    if (!decided) decided = readStored() ?? live;
    if (decided !== group) setGroup(decided);
  }, [live]);

  // Keep the stored group current for the next page load (rollout changes, flag turned off).
  useEffect(() => {
    if (!live) return;
    try {
      localStorage.setItem(STORAGE_KEY, live);
    } catch {
      // storage blocked
    }
  }, [live]);

  useEffect(() => {
    if (!group || loggedSurfaces.has(surface)) return;
    loggedSurfaces.add(surface);
    try {
      splunkInstance('booking').sendEvent({ group: 'price_vat_ab', type: 'exposure', event: { surface, ab_group: group } });
    } catch {
      // analytics must never break the page
    }
  }, [group, surface]);

  return group === 'with_vat';
};

/** Same as `withOnlineVisitVat` for an API price string such as "۹۸,۰۰۰ تومان" (keeps the rest of the text). */
export const withOnlineVisitVatText = <T,>(text: T, includeVat: boolean): T => {
  if (!includeVat || typeof text !== 'string') return text;
  return text.replace(/[0-9۰-۹][0-9۰-۹,٬]*/, num => {
    const n = parseInt(num.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[,٬]/g, ''), 10);
    return withOnlineVisitVat(n, true).toLocaleString('fa-IR');
  }) as T;
};
